import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Plus, ArrowUpCircle, Clock, CheckCircle, XCircle, FileText, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (n) => `R ${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const statusBadge = (s) => {
  const map = {
    pending:      { cls: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock },
    acknowledged: { cls: 'bg-blue-100 text-blue-800 border-blue-200', icon: CheckCircle },
    matched:      { cls: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
    rejected:     { cls: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
  };
  const cfg = map[s] || map.pending;
  const Icon = cfg.icon;
  return (
    <Badge className={`${cfg.cls} border flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {s.charAt(0).toUpperCase() + s.slice(1)}
    </Badge>
  );
};

export default function ContributionRequestsPage() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API_URL}/api/investor/contribution-requests`, authHeader);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error('Failed to load contributions');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  return (
    <div className="space-y-6" data-testid="contribution-requests-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#212529] font-['Manrope']">Additions</h1>
          <p className="text-[#6C757D] mt-1">Flag any additional funds you are contributing to your portfolio.</p>
        </div>
        <Button onClick={() => setShowModal(true)} data-testid="new-contribution-btn" className="bg-[#18A37C] hover:bg-[#1D2D2A] text-white">
          <Plus className="h-4 w-4 mr-2" /> New Contribution
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><ArrowUpCircle className="h-5 w-5 text-[#18A37C]" /> Flagged Contributions</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center text-[#6C757D]"><Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading…</div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-[#6C757D]">No contribution requests yet. Click <em>New Contribution</em> above to flag incoming funds.</div>
          ) : (
            <div className="space-y-3">
              {items.map(r => (
                <div key={r.id} className="border rounded-lg p-4 flex flex-wrap items-start justify-between gap-3" data-testid={`contribution-row-${r.id}`}>
                  <div>
                    <div className="text-lg font-semibold text-[#1D2D2A]">{formatCurrency(r.amount)}</div>
                    <div className="text-xs text-[#6C757D] mt-1">
                      Ref: {r.payment_reference || '—'} · Expected {r.expected_date || '—'} · Filed {new Date(r.created_at).toLocaleDateString('en-ZA')}
                    </div>
                    {r.notes && <div className="text-sm text-[#6C757D] mt-2">{r.notes}</div>}
                    {r.proof_of_payment_name && <div className="text-xs text-[#18A37C] mt-2 flex items-center gap-1"><FileText className="h-3 w-3" /> {r.proof_of_payment_name}</div>}
                  </div>
                  <div className="text-right">
                    {statusBadge(r.status)}
                    {r.admin_notes && <div className="text-xs text-[#6C757D] mt-2 max-w-xs">Admin: {r.admin_notes}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ContributionModal open={showModal} onOpenChange={setShowModal} onSubmitted={load} authHeader={authHeader} />
    </div>
  );
}

function ContributionModal({ open, onOpenChange, onSubmitted, authHeader }) {
  const [amount, setAmount] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [ref, setRef] = useState('');
  const [notes, setNotes] = useState('');
  const [popFile, setPopFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setAmount(''); setExpectedDate(''); setRef(''); setNotes(''); setPopFile(null); };

  const submit = async () => {
    if (!amount || Number(amount) <= 0) { toast.error('Amount is required'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/investor/contribution-requests`, {
        amount: Number(amount),
        expected_date: expectedDate || null,
        payment_reference: ref || null,
        proof_of_payment_name: popFile?.name || null,
        proof_of_payment_key: null,
        notes: notes || null,
      }, authHeader);

      // Upload the actual PoP file if the investor attached one
      if (popFile) {
        const reqId = res.data?.request?.id;
        if (reqId) {
          const fd = new FormData();
          fd.append('file', popFile);
          try {
            await axios.post(
              `${API_URL}/api/investor/contribution-requests/${reqId}/proof-of-payment`,
              fd,
              { ...authHeader, headers: { ...authHeader.headers, 'Content-Type': 'multipart/form-data' } }
            );
          } catch (uploadErr) {
            toast.warning('Contribution flagged, but proof of payment upload failed. You can retry later.');
            reset(); onOpenChange(false); onSubmitted?.();
            return;
          }
        }
      }

      toast.success(
        popFile
          ? 'Contribution flagged with proof of payment. Investor Relations has been notified.'
          : 'Contribution flagged. Investor Relations has been notified.'
      );
      reset(); onOpenChange(false); onSubmitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Flag a new contribution</DialogTitle>
          <DialogDescription>
            Let us know about incoming funds. Investor Relations and admin will be notified so we can match your payment against the incoming bank transaction.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Amount (ZAR) *</Label><Input data-testid="contribution-amount" type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="e.g. 100000" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Expected payment date</Label><Input data-testid="contribution-date" type="date" value={expectedDate} onChange={e => setExpectedDate(e.target.value)} /></div>
            <div><Label>Payment reference</Label><Input data-testid="contribution-ref" value={ref} onChange={e => setRef(e.target.value)} placeholder="Bank reference you will use" /></div>
          </div>
          <div>
            <Label>Proof of payment</Label>
            <Input data-testid="contribution-pop" type="file" onChange={e => setPopFile(e.target.files?.[0] || null)} accept="application/pdf,image/*" />
            {popFile && <div className="text-xs text-[#6C757D] mt-1">Attached: {popFile.name} · {(popFile.size / 1024).toFixed(1)} KB</div>}
            <div className="text-xs text-[#6C757D] mt-1">Attach a bank slip, screenshot or PDF. The file is stored securely and will be visible to Investor Relations and admin.</div>
          </div>
          <div><Label>Notes</Label><Textarea data-testid="contribution-notes" rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Anything the ops team should know" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="contribution-submit" className="bg-[#18A37C] hover:bg-[#1D2D2A] text-white">
            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</> : 'Submit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
